import { LCDocumentEditorUtils } from './LCDocumentEditorUtils';
import { EditorAttachedFilePlugin } from './EditorAttachedFilePlugin';
import { LectioEditorLinkPlugin } from './LectioEditorLinkPlugin';
import { SelectionDialogs } from './../SelectionDialogs';
import { LCLinkTitleLoop } from './LCLinkTitleLoop';
import { ILectioPlugin, LinkDialogData } from './Types';
import { LectioJSUtils } from '../LectioJSUtils';
import { LCDialogEditorUtils } from './LCEditorDialogUtils';

export class PluginLectioLink implements ILectioPlugin {
	public static hiding: Rx.Subject<any>;
	public Init(): void {
		PluginLectioLink.hiding = new Rx.Subject<any>();
		CKEDITOR.plugins.add('lectiolink', {
			init: (editorOuter: CKEDITOR.editor) => {
				// Dette plugin låner en del (opførsel, ikoner, tekst) fra ckeditors eget link-plugin.
				const getDialogDef = (editor: CKEDITOR.editor) => {

					const def: CKEDITOR.dialog.IDialogDefinition = {
						title: "Egenskaber for link",
						minWidth: 400,
						minHeight: 150,
						contents: [
							{
								id: 'tab1',
								elements: [
									{
										type: 'text',
										label: 'Link',
										id: 'url',
										validate: CKEDITOR.dialog.validate.notEmpty('Der skal angives et link.'),
										setup(data: LinkDialogData) {
											this.setValue(data.href);
										},
										commit(data: LinkDialogData) {
											data.href = this.getValue().trim();
										}
									},
									{
										type: 'text',
										label: 'Titel',
										id: 'text',
										commit(data: LinkDialogData) {
											data.text = this.getValue().trim();
										}
									}, {
										type: 'html',
										html: '<div id="statusDiv"></div>'
									} as any
								]
							}
						],
						onShow() {
							const selection = editor.getSelection()!;
							const existingLink = LectioEditorLinkPlugin.GetSelectedLink(editor);

							const range = selection.getRanges()[0];
							const textEditSupported = range.collapsed;

							const dialogData: Partial<LinkDialogData> = {
								href: existingLink?.getAttribute('href') ?? undefined,
								text: undefined
							};

							const dialog = this as CKEDITOR.dialog & {
								_: { selectedElement: CKEDITOR.dom.element | null }
							};
							dialog.once('hide', () => PluginLectioLink.hiding.onNext(null));

							dialog._.selectedElement = existingLink;
							const titleUIElement = dialog.getContentElement('tab1', 'text');

							const urlUIElement = dialog.getContentElement('tab1', 'url');
							Rx.Observable.interval(1000)
								.takeUntil(PluginLectioLink.hiding)
								.select(_ => (LectioJSUtils.GetAssertedType(urlUIElement.getValue(), String, 'boo')).trim())
								.distinctUntilChanged()
								.subscribe(url => {
									if (url.match(/^lh-\d{2,5}$/i)) {
										urlUIElement.setValue('/lectio/help/article/' + url);
									}
								});

							if (!textEditSupported) {
								// a-elementet kan omfatte andre elementer, så det giver ikke nødvendigvis mening at redigere tekstindholdet.
								titleUIElement.disable();
							} else {
								const statusDiv = dialog.getElement().getDocument().getById('statusDiv');
								if (!statusDiv)
									throw new Error('statusDiv mangler.');
								const linkValObs = Rx.Observable.interval(1000)
									.takeUntil(PluginLectioLink.hiding)
									.select(_ => (LectioJSUtils.GetAssertedType(urlUIElement.getValue(), String, 'boo')).trim())
									.distinctUntilChanged()
									.debounce(300) // debounce always returns the last one, throttle would eat anything in the 300 ms interval.
									;

								LCLinkTitleLoop.create(linkValObs, titleUIElement, (text, status) => {
									statusDiv.setText(text);
									dialog.setState(status ? CKEDITOR.DIALOG_STATE_IDLE : CKEDITOR.DIALOG_STATE_BUSY);
								});
							}

							dialog.setupContent(dialogData);
						},
						onOk: function () {
							const dialog = this as CKEDITOR.dialog & {
								_: { selectedElement?: CKEDITOR.dom.element }
							};
							const _ = dialog._;
							const data: Partial<LinkDialogData> = {};
							dialog.commitContent(data);

							const hrefInitial = data.href ?? '';
							const hrefAdjusted = LectioEditorLinkPlugin.GetAdjustedHref(hrefInitial);
							const adjustedText = data.text ?? '';
							// Flg. er taget fra link-plugin-et og tilpasset.
							if (!dialog._.selectedElement) {
								LectioEditorLinkPlugin.InsertLink(editor, hrefInitial, hrefAdjusted, data.text ?? '');
							} else {
								// We're only editing an existing link, so just overwrite the attributes.
								const element = dialog._.selectedElement;
								if (adjustedText !== '') {
									element.setText(adjustedText);
								}
								element.setAttributes({ href: hrefAdjusted});
								// Denne sætter ckeditor på, i hvert fald når man har været i source view.
								// hvis man ikke fjerne den, bliver værdien flyttet til href-attributten, når man beder om html-en (!).
								element.removeAttribute("data-cke-saved-href");

								delete _.selectedElement;
							}
						}
					};
					return def;
				};

				if (editorOuter.contextMenu) {
					editorOuter.addMenuItems({
						link: {
							label: editorOuter.lang.link.menu,
							command: 'link',
							group: 'link',
							icon: 'lectioinsertlink',
							order: 1,
						},
						unlink: {
							label: editorOuter.lang.link.unlink,
							command: 'unlink',
							group: 'link',
							icon: 'lectiounlink',
							order: 5,
						}
					});

					editorOuter.contextMenu.addListener(element => {
						if (!element || element.isReadOnly())
							return null;

						let anchor: CKEDITOR.dom.element | null = CKEDITOR.plugins.link.tryRestoreFakeAnchor(editorOuter, element);

						if (!anchor) {
							anchor = CKEDITOR.plugins.link.getSelectedLink(editorOuter) as any;
							if (!anchor)
								return null;
						}

						const menu: { [command: string]: number | undefined } = {};

						const href = anchor.getAttribute('href');
						if (href && anchor.getChildCount()) {
							const isResource = !!LCDocumentEditorUtils.TryGetResourceFilename(href);
							if (!isResource)
								menu.unlink = CKEDITOR.TRISTATE_OFF;
							if (!LCDocumentEditorUtils.TryGetResourceFilename(href))
								menu.link = CKEDITOR.TRISTATE_OFF;
						}

						return menu;
					});
				}

				CKEDITOR.dialog.add('lectiolinkDialog', getDialogDef);
				const linkCmd = new (CKEDITOR as any).dialogCommand('lectiolinkDialog', {
					allowedContent: 'a[!href]',
					requiredContent: 'a[href]'
				});

				const cfg = LCDocumentEditorUtils.GetEditorConfig(editorOuter);

				editorOuter.ui.addButton('lectiolinkButton', {
					label: editorOuter.lang.link.toolbar,
					command: 'link',
					icon: 'lectioinsertlink',
					toolbar: ''
				});
				editorOuter.ui.addButton('lectiounlinkButton', {
					label: editorOuter.lang.link.unlink,
					command: 'unlink',
					icon: 'lectiounlink',
					toolbar: ''
				});

				editorOuter.addCommand('link', {
					exec: editor => {
						if (LectioEditorLinkPlugin.GetSelectedLink(editor)) {
							editor.execCommand('lectiolinkDialog');
							return false;
						}
						const editorConfig = LCDocumentEditorUtils.GetEditorConfig(editor);
						const resp = LCDialogEditorUtils.ShowSuitableContentPicker(
							editor,
							{ folderid: SelectionDialogs.InitialFolder.createLink },
							editorConfig);

						resp.promise.then(selection => {
							if (!selection)
								return;
							LCDialogEditorUtils.ProcessNewHomeworkSelection(
								editorOuter, selection,
								editorConfig.handleHomeworkPickerSelection,
								resp.editorHadFocus);
						});

						return true;
					}
				});

				editorOuter.addCommand('lectiolinkDialog', linkCmd);
				editorOuter.addCommand('unlink', new CKEDITOR.unlinkCommand());
				// Ligesom word.
				editorOuter.setKeystroke(CKEDITOR.CTRL + 75 /*K*/, 'link');
			}
		});
	}
}
